import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Snackbar, Stack, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import {
  adminAPI,
  fetchSystemHealth,
  productSubmissionsAPI,
  supportTicketsAPI,
  waitlistAPI,
} from '../services/api';
import { tokens } from '../theme';
import AdminShell from '../components/admin/AdminShell';
import AdminOverviewView from '../components/admin/AdminOverviewView';
import AdminOperationsView from '../components/admin/AdminOperationsView';
import AdminWaitlistOperationsView from '../components/admin/AdminWaitlistOperationsView';
import AdminSystemHealthView from '../components/admin/AdminSystemHealthView';
import {
  ADMIN_ANALYTICS_SECTIONS,
  AdminSettingsAnalytics,
  CustomersAnalytics,
  ReviewAnalytics,
  SubscriptionsAnalytics,
} from '../components/dashboard/analytics/AdminAnalyticsScreens';

export default function AdminDashboardEnhanced() {
  const navigate = useNavigate();
  const { section: sectionParam } = useParams();
  const activeView = ADMIN_ANALYTICS_SECTIONS.includes(sectionParam) ? sectionParam : null;
  const { user: currentUser, logout } = useAuth();

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState(null);
  const [overviewAnalytics, setOverviewAnalytics] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [userProfiles, setUserProfiles] = useState({});
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [selectedWaitlistEntryId, setSelectedWaitlistEntryId] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [replyMode, setReplyMode] = useState('reply');
  const [caseTags, setCaseTags] = useState([]);
  const [queueFilter, setQueueFilter] = useState('all');
  const [queueSort, setQueueSort] = useState('newest');
  const [operationsWorkspace, setOperationsWorkspace] = useState('cases');
  const [waitlistStatusFilter, setWaitlistStatusFilter] = useState('all');
  const [waitlistPlacementFilter, setWaitlistPlacementFilter] = useState('all');
  const [waitlistSourceFilter, setWaitlistSourceFilter] = useState('all');
  const [waitlistSubmissionFilter, setWaitlistSubmissionFilter] = useState('all');
  const [waitlistExporting, setWaitlistExporting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [
        statsRes,
        overviewRes,
        usersRes,
        subscriptionsRes,
        ticketsRes,
        submissionsRes,
        waitlistRes,
      ] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getOverviewAnalytics({ days: 30 }),
        adminAPI.getUsers({ limit: 200, includeSubscription: true }),
        adminAPI.getSubscriptions({ limit: 200 }),
        supportTicketsAPI.list(),
        productSubmissionsAPI.list({ status: 'pending' }),
        waitlistAPI.list({ limit: 500 }),
      ]);
      const statsData = statsRes.data?.data || null;
      setStats(statsData);
      setOverviewAnalytics(overviewRes.data?.data || null);
      setSystemHealth(await fetchSystemHealth(statsData));
      setUsers(usersRes.data?.data?.items || []);
      setSubscriptions(subscriptionsRes.data?.data?.items || []);
      setTickets(ticketsRes.data?.data || []);
      setSubmissions(submissionsRes.data?.data || []);
      setWaitlistEntries(waitlistRes.data?.data?.items || []);
    } catch (err) {
      setError(err.message || 'Failed to load admin dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const showSnackbar = useCallback((message) => {
    setSnackbar({ open: true, message });
  }, []);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/');
  }, [logout, navigate]);

  const navigateToView = useCallback((view) => {
    if (view === 'waitlist') {
      setOperationsWorkspace('waitlist');
    } else if (view === 'operations') {
      setOperationsWorkspace('cases');
    }
    navigate(`/admin/${view}`);
  }, [navigate]);

  const exportWaitlistCsv = useCallback(async () => {
    setWaitlistExporting(true);
    try {
      const status = waitlistStatusFilter !== 'all' ? waitlistStatusFilter : undefined;
      await waitlistAPI.downloadCsv(
        status ? { status } : {},
        `fasttime-waitlist-${status || 'all'}-${new Date().toISOString().slice(0, 10)}.csv`
      );
      showSnackbar('Waitlist CSV exported.');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Waitlist export failed');
    } finally {
      setWaitlistExporting(false);
    }
  }, [showSnackbar, waitlistStatusFilter]);

  const unresolvedTickets = tickets.filter((t) => t.status !== 'resolved' && t.status !== 'closed');

  const selectedCase = useMemo(() => {
    if (!selectedCaseId) return null;
    const ticketMatch = selectedCaseId.match(/^ticket:(.+)$/);
    if (ticketMatch) return tickets.find((t) => t.id === ticketMatch[1]) || null;
    return null;
  }, [selectedCaseId, tickets]);

  const selectedUserId = useMemo(() => {
    if (!selectedCaseId) return null;
    if (selectedCaseId.startsWith('ticket:')) return selectedCase?.userId;
    if (selectedCaseId.startsWith('submission:')) {
      const id = selectedCaseId.replace('submission:', '');
      return submissions.find((s) => (s.id || s._id) === id)?.submittedBy;
    }
    if (selectedCaseId.startsWith('subscription:')) return selectedCaseId.replace('subscription:', '');
    return null;
  }, [selectedCaseId, selectedCase, submissions]);

  useEffect(() => {
    if (!selectedUserId) return;
    let cancelled = false;
    adminAPI.getUserProfile(selectedUserId)
      .then((res) => {
        if (!cancelled) {
          setUserProfiles((prev) => ({ ...prev, [selectedUserId]: res.data?.data || null }));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedUserId]);

  useEffect(() => {
    if (selectedCase) {
      setReplyDraft(selectedCase.adminResponse || '');
      setCaseTags(selectedCase.tags || []);
    } else {
      setReplyDraft('');
      setCaseTags([]);
    }
  }, [selectedCase]);

  useEffect(() => {
    if (activeView !== 'operations') return;
    const openTickets = tickets.filter((t) => t.status !== 'resolved' && t.status !== 'closed');
    if (!selectedCaseId && openTickets.length > 0) {
      setSelectedCaseId(`ticket:${openTickets[0].id}`);
    }
  }, [activeView, tickets, selectedCaseId]);

  useEffect(() => {
    if (activeView === 'waitlist') {
      setOperationsWorkspace('waitlist');
    } else if (activeView === 'operations') {
      setOperationsWorkspace((prev) => prev || 'cases');
    }
  }, [activeView]);

  const handleSupportUpdate = useCallback(async (payload, message) => {
    if (!selectedCase) return;
    setUpdating(true);
    try {
      await supportTicketsAPI.update(selectedCase.id, payload);
      showSnackbar(message);
      await loadData();
    } catch (err) {
      setError(err.message || 'Could not update ticket');
    } finally {
      setUpdating(false);
    }
  }, [loadData, selectedCase, showSnackbar]);

  const handleSubmissionById = useCallback(async (submission, action) => {
    const submissionId = submission.id || submission._id;
    if (!submissionId) return;
    setUpdating(true);
    try {
      if (action === 'approve') {
        await productSubmissionsAPI.verify(submissionId);
        showSnackbar('Submission approved.');
      } else {
        await productSubmissionsAPI.reject(submissionId);
        showSnackbar('Submission rejected.');
      }
      await loadData();
    } catch (err) {
      setError(err.message || 'Could not update submission');
    } finally {
      setUpdating(false);
    }
  }, [loadData, showSnackbar]);

  const handleSubscriptionUpdate = useCallback(async (userId, payload, message) => {
    if (!userId) return;
    setUpdating(true);
    try {
      await adminAPI.updateSubscription(userId, payload);
      showSnackbar(message);
      await loadData();
    } catch (err) {
      setError(err.message || 'Could not update subscription');
    } finally {
      setUpdating(false);
    }
  }, [loadData, showSnackbar]);

  const handleUserStatus = useCallback(async (userId, isActive) => {
    if (!userId) return;
    setUpdating(true);
    try {
      await adminAPI.updateUserStatus(userId, isActive);
      showSnackbar(isActive ? 'Account restored.' : 'Account suspended.');
      await loadData();
    } catch (err) {
      setError(err.message || 'Could not update account status');
    } finally {
      setUpdating(false);
    }
  }, [loadData, showSnackbar]);

  const handleBanUser = useCallback(async (userId, isBanned) => {
    if (!userId) return;
    setUpdating(true);
    try {
      await adminAPI.banUser(userId, isBanned);
      showSnackbar(isBanned ? 'Account banned.' : 'Account unbanned.');
      await loadData();
    } catch (err) {
      setError(err.message || 'Could not update ban status');
    } finally {
      setUpdating(false);
    }
  }, [loadData, showSnackbar]);

  const openCustomerInOperations = useCallback((customer) => {
    const ticket = tickets.find((t) => t.userId === customer.id);
    if (ticket) setSelectedCaseId(`ticket:${ticket.id}`);
    else {
      const sub = subscriptions.find((s) => s.userId === customer.id);
      if (sub) setSelectedCaseId(`subscription:${sub.userId}`);
    }
    navigateToView('operations');
  }, [navigateToView, subscriptions, tickets]);

  if (!activeView) {
    return <Navigate to="/admin/overview" replace />;
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: tokens.gradientHero }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress sx={{ color: tokens.accentStrong }} />
          <Typography variant="body2" sx={{ color: tokens.textSoft }}>Loading admin workspace...</Typography>
        </Stack>
      </Box>
    );
  }

  const selectedUser = users.find((u) => u.id === selectedUserId) || null;

  const adminFooter = (
    <Typography variant="caption" sx={{ color: tokens.textMuted }}>
      FastTime Admin · All times shown in Pacific Time (PT)
    </Typography>
  );

  return (
    <>
      <AdminShell
        activeView={activeView}
        currentUser={currentUser}
        search={search}
        onSearchChange={setSearch}
        onNavigate={navigateToView}
        onRefresh={loadData}
        onOpenOperations={() => navigateToView('operations')}
        onLogout={handleLogout}
        badges={{ tickets: unresolvedTickets.length, submissions: submissions.length }}
        footer={adminFooter}
        contentFill={activeView === 'operations' || activeView === 'waitlist'}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError('')}>{error}</Alert>
        )}

        {activeView === 'overview' && (
          <AdminOverviewView
            stats={stats}
            overviewAnalytics={overviewAnalytics}
            users={users}
            subscriptions={subscriptions}
            tickets={tickets}
            submissions={submissions}
            systemHealth={systemHealth}
            onOpenOperations={() => navigateToView('operations')}
            onOpenSystemHealth={() => navigateToView('system')}
            onApproveSubmission={(s) => handleSubmissionById(s, 'approve')}
            onRejectSubmission={(s) => handleSubmissionById(s, 'reject')}
            updating={updating}
          />
        )}

        {activeView === 'operations' && (
          <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                variant={operationsWorkspace === 'cases' ? 'contained' : 'outlined'}
                onClick={() => setOperationsWorkspace('cases')}
              >
                Case queue
              </Button>
              <Button
                variant={operationsWorkspace === 'waitlist' ? 'contained' : 'outlined'}
                onClick={() => setOperationsWorkspace('waitlist')}
              >
                Waitlist ops
              </Button>
            </Stack>

            {operationsWorkspace === 'waitlist' ? (
              <AdminWaitlistOperationsView
                entries={waitlistEntries}
                loading={loading}
                error={error}
                search={search}
                statusFilter={waitlistStatusFilter}
                onStatusFilterChange={setWaitlistStatusFilter}
                placementFilter={waitlistPlacementFilter}
                onPlacementFilterChange={setWaitlistPlacementFilter}
                sourceFilter={waitlistSourceFilter}
                onSourceFilterChange={setWaitlistSourceFilter}
                submissionFilter={waitlistSubmissionFilter}
                onSubmissionFilterChange={setWaitlistSubmissionFilter}
                selectedEntryId={selectedWaitlistEntryId}
                onSelectEntry={setSelectedWaitlistEntryId}
                onExport={exportWaitlistCsv}
                exporting={waitlistExporting}
              />
            ) : (
              <AdminOperationsView
                tickets={tickets}
                submissions={submissions}
                subscriptions={subscriptions}
                users={users}
                userProfiles={userProfiles}
                search={search}
                selectedCaseId={selectedCaseId}
                onSelectCase={setSelectedCaseId}
                replyDraft={replyDraft}
                onReplyChange={setReplyDraft}
                replyMode={replyMode}
                onReplyModeChange={setReplyMode}
                caseTags={caseTags}
                onCaseTagsChange={(tags) => {
                  setCaseTags(tags);
                  if (selectedCase) handleSupportUpdate({ tags }, 'Tags updated.');
                }}
                queueFilter={queueFilter}
                onQueueFilterChange={setQueueFilter}
                queueSort={queueSort}
                onQueueSortChange={setQueueSort}
                updating={updating}
                onSendReply={() => handleSupportUpdate({ adminResponse: replyDraft }, 'Reply sent.')}
                onInternalNote={() => handleSupportUpdate({ internalNote: replyDraft }, 'Internal note saved.')}
                onMarkInProgress={() => handleSupportUpdate({ status: 'inProgress', keepOpen: true }, 'Ticket marked in progress.')}
                onApproveSubmission={() => {
                  const id = selectedCaseId?.replace('submission:', '');
                  const sub = submissions.find((s) => (s.id || s._id) === id);
                  if (sub) handleSubmissionById(sub, 'approve');
                }}
                onRejectSubmission={() => {
                  const id = selectedCaseId?.replace('submission:', '');
                  const sub = submissions.find((s) => (s.id || s._id) === id);
                  if (sub) handleSubmissionById(sub, 'reject');
                }}
                onGrantPremium={() => handleSubscriptionUpdate(selectedUserId, {
                  isPremium: true,
                  status: 'active',
                  platform: selectedUser?.subscription?.platform || 'ios',
                  expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
                }, 'Premium access granted.')}
                onMarkExpired={() => handleSubscriptionUpdate(selectedUserId, { isPremium: false, status: 'expired' }, 'Subscription marked expired.')}
                onSyncStore={async () => {
                  if (!selectedUserId) return;
                  setUpdating(true);
                  try {
                    await adminAPI.syncSubscription(selectedUserId);
                    showSnackbar('Subscription synced with store.');
                    await loadData();
                  } catch (err) {
                    setError(err.message || 'Sync failed');
                  } finally {
                    setUpdating(false);
                  }
                }}
                onRefund={async () => {
                  if (!selectedUserId) return;
                  setUpdating(true);
                  try {
                    await adminAPI.refundSubscription(selectedUserId, { reason: 'Admin refund' });
                    showSnackbar('Refund recorded.');
                    await loadData();
                  } catch (err) {
                    setError(err.message || 'Refund failed');
                  } finally {
                    setUpdating(false);
                  }
                }}
                onAddCredit={async (amountCents) => {
                  if (!selectedUserId || !amountCents) return;
                  setUpdating(true);
                  try {
                    await adminAPI.addAccountCredit(selectedUserId, amountCents);
                    showSnackbar(`$${(amountCents / 100).toFixed(2)} account credit added.`);
                    await loadData();
                  } catch (err) {
                    setError(err.message || 'Credit failed');
                  } finally {
                    setUpdating(false);
                  }
                }}
                onMakeAdmin={async (userId) => {
                  if (!userId || userId === currentUser?.id) return;
                  setUpdating(true);
                  try {
                    await adminAPI.makeAdmin(userId);
                    showSnackbar('Admin access granted.');
                    await loadData();
                  } catch (err) {
                    setError(err.message || 'Could not grant admin');
                  } finally {
                    setUpdating(false);
                  }
                }}
                onRemoveAdmin={async (userId) => {
                  if (!userId || userId === currentUser?.id) return;
                  setUpdating(true);
                  try {
                    await adminAPI.removeAdmin(userId);
                    showSnackbar('Admin access removed.');
                    await loadData();
                  } catch (err) {
                    setError(err.message || 'Could not remove admin');
                  } finally {
                    setUpdating(false);
                  }
                }}
                onCancelSubscription={() => handleSubscriptionUpdate(selectedUserId, { status: 'cancelled', isPremium: false }, 'Subscription cancelled.')}
                onSuspendAccount={() => handleUserStatus(selectedUserId, selectedUser?.isActive === false)}
                onBanAccount={() => handleBanUser(selectedUserId, !selectedUser?.isBanned)}
                onNavigateOverview={() => navigateToView('overview')}
              />
            )}
          </Stack>
        )}

        {activeView === 'customers' && (
          <CustomersAnalytics users={users} onSelectCustomer={openCustomerInOperations} />
        )}

        {activeView === 'subscriptions' && (
          <SubscriptionsAnalytics subscriptions={subscriptions} users={users} />
        )}

        {activeView === 'waitlist' && (
          <AdminWaitlistOperationsView
            entries={waitlistEntries}
            loading={loading}
            error={error}
            search={search}
            statusFilter={waitlistStatusFilter}
            onStatusFilterChange={setWaitlistStatusFilter}
            placementFilter={waitlistPlacementFilter}
            onPlacementFilterChange={setWaitlistPlacementFilter}
            sourceFilter={waitlistSourceFilter}
            onSourceFilterChange={setWaitlistSourceFilter}
            submissionFilter={waitlistSubmissionFilter}
            onSubmissionFilterChange={setWaitlistSubmissionFilter}
            selectedEntryId={selectedWaitlistEntryId}
            onSelectEntry={setSelectedWaitlistEntryId}
            onExport={exportWaitlistCsv}
            exporting={waitlistExporting}
          />
        )}

        {activeView === 'review' && (
          <ReviewAnalytics
            submissions={submissions}
            updating={updating}
            onApprove={(s) => handleSubmissionById(s, 'approve')}
            onReject={(s) => handleSubmissionById(s, 'reject')}
          />
        )}

        {activeView === 'system' && <AdminSystemHealthView />}

        {activeView === 'settings' && <AdminSettingsAnalytics />}
      </AdminShell>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3200}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
      />
    </>
  );
}
